import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import LoginHeader from './components/LoginHeader';
import EnvironmentBadge from './components/EnvironmentBadge';
import LoginForm from './components/LoginForm';
import SessionInfo from './components/SessionInfo';
import SecurityFeatures from './components/SecurityFeatures';
import { LoginFormData, LoginFormErrors, SessionInfo as SessionInfoType, AuthResponse } from './types';

const LoginScreen = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [sessionInfo] = useState<SessionInfoType>({
    lastLogin: '15/01/2025 às 14:30',
    concurrentSessions: 0,
    environment: 'homologation'
  });

  const mockCredentials = {
    analyst: { email: 'analista@empresa.com.br', password: 'analista123' },
    manager: { email: 'gerente@empresa.com.br', password: 'gerente123' },
    admin: { email: 'admin@empresa.com.br', password: 'admin123' }
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      navigate('/tax-consultation-dashboard');
    }
  }, [navigate]);

  const authenticateUser = (email: string, password: string): AuthResponse => {
    const credentials = Object.entries(mockCredentials).find(
      ([, cred]) => cred.email === email && cred.password === password
    );

    if (!credentials) {
      return {
        success: false,
        error: 'Email ou senha inválidos. Verifique suas credenciais e tente novamente.'
      };
    }

    const [role] = credentials;
    const mockToken = `mock-jwt-token-${Date.now()}`;
    
    return {
      success: true,
      token: mockToken,
      user: {
        id: `user-${role}-${Date.now()}`,
        name: role === 'analyst' ? 'João Silva' : role === 'manager' ? 'Maria Santos' : 'Carlos Admin',
        email,
        role: role as 'analyst' | 'manager' | 'admin'
      }
    };
  };

  const handleLogin = async (formData: LoginFormData) => {
    setLoading(true);
    setErrors({});

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));

      const authResponse = authenticateUser(formData.email, formData.password);

      if (!authResponse.success) {
        setErrors({ general: authResponse.error });
        setLoading(false);
        return;
      }

      if (authResponse.token && authResponse.user) {
        localStorage.setItem('authToken', authResponse.token);
        localStorage.setItem('userRole', authResponse.user.role);
        localStorage.setItem('userName', authResponse.user.name);
        localStorage.setItem('userEmail', authResponse.user.email);
        
        if (formData.rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }

        const roleRedirects = {
          analyst: '/tax-consultation-dashboard',
          manager: '/tax-consultation-dashboard',
          admin: '/tax-consultation-dashboard'
        };

        navigate(roleRedirects[authResponse.user.role]);
      }
    } catch (error) {
      setErrors({
        general: 'Erro ao conectar com o servidor. Tente novamente em alguns instantes.'
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setErrors({});
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <>
      <Helmet>
        <title>Login - TaxQuery Pro</title>
        <meta name="description" content="Acesso seguro ao sistema de consulta tributária corporativa" />
      </Helmet>

      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-lg shadow-card p-8 relative">
            <EnvironmentBadge environment={sessionInfo.environment} />
            
            <LoginHeader />
            
            <LoginForm
              onSubmit={handleLogin}
              loading={loading}
              errors={errors}
            />
            
            <SessionInfo
              lastLogin={sessionInfo.lastLogin}
              concurrentSessions={sessionInfo.concurrentSessions}
            />
            
            <SecurityFeatures />
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground">
              &copy; {new Date().getFullYear()} TaxQuery Pro. Todos os direitos reservados.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Versão 1.0.0 | Suporte: suporte@taxquerypro.com.br
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginScreen;